import Keycloak from 'keycloak-js';

// The Keycloak client a session logs in through. There's the deploy's
// *default* one (REACT_APP_KEYCLOAK_* below) and, for an organization on
// its own Keycloak instance, the one /api/auth/resolve-provider hands the
// login page (see views/Login/IdentifierFirstLogin.tsx) - several realms
// (so several issuers) can be in play at once, unlike Auth0 which stays a
// single client. `issuer` is what a token's `iss` claim carries; it's the
// identity of a client everywhere here. Mirrors the public part of the
// backend's config (admin/api/authentication/keycloak_config.py).
export interface KeycloakClientConfig {
  issuer: string;
  url: string;
  realm: string;
  clientId: string;
}

// trim() plus dropping trailing slashes, in one linear pass - the obvious
// `.replace(/\/+$/, '')` backtracks quadratically on a long run of slashes.
const trimUrl = (value: string): string => {
  let end = value.trimEnd().length;
  while (end > 0 && value[end - 1] === '/') {
    end -= 1;
  }
  return value.slice(0, end).trimStart();
};

// Builds a config from the raw public fields, deriving the issuer the way
// Keycloak stamps it (`${url}/realms/${realm}`) unless one is given -
// e.g. because Keycloak sits behind a proxy that rewrites it. Returns
// undefined for anything incomplete rather than a config that can't work.
export const toKeycloakClientConfig = (
  raw: unknown
): KeycloakClientConfig | undefined => {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const entry = raw as Record<string, unknown>;
  const url = typeof entry.url === 'string' ? trimUrl(entry.url) : '';
  const realm = typeof entry.realm === 'string' ? entry.realm.trim() : '';
  // Both spellings: the backend's public_config() (client_id) and our own
  // persisted shape (clientId).
  const rawClientId = entry.clientId ?? entry.client_id;
  const clientId = typeof rawClientId === 'string' ? rawClientId.trim() : '';
  if (!url || !realm || !clientId) {
    return undefined;
  }
  const issuer =
    typeof entry.issuer === 'string' && entry.issuer.trim()
      ? trimUrl(entry.issuer)
      : `${url}/realms/${realm}`;
  return { issuer, url, realm, clientId };
};

// Local-dev-only second identity provider - see
// docker-compose.exp-core-keycloak.yml. These env vars are optional (unlike the
// required Auth0 ones in utils/util/config.ts): most orgs never touch
// this path, so there's nothing to validate for them. Undefined when
// not (fully) set, e.g. every deployed environment today.
export const getDefaultKeycloakClientConfig = ():
  | KeycloakClientConfig
  | undefined =>
  toKeycloakClientConfig({
    url: process.env.REACT_APP_KEYCLOAK_URL,
    realm: process.env.REACT_APP_KEYCLOAK_REALM,
    clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
    issuer: process.env.REACT_APP_KEYCLOAK_ISSUER,
  });

// Marks which provider the user last authenticated with - and, for
// Keycloak, through which client - so a page reload/return visit knows
// whether to re-establish an Auth0 or a Keycloak session (and against
// which realm) instead of defaulting to Auth0. keycloak-js needs the
// client config *before* it can run its silent check-sso, i.e. before
// there is any token to read an issuer from, which is why the whole
// (public) config is persisted and not just a flag. Written by
// KeycloakAuthContext.tsx right before redirecting to Keycloak, by
// IdentifierFirstLogin.tsx before redirecting to Auth0, and by Logout.tsx
// on sign-out.
const ACTIVE_AUTH_PROVIDER_KEY = 'confirm.active_auth_provider';
const ACTIVE_KEYCLOAK_CLIENT_KEY = 'confirm.active_keycloak_client';

export type AuthProviderName = 'auth0' | 'keycloak';

/**
 * The Keycloak client the current session belongs to, if the last login
 * was via Keycloak: the one persisted at login, else (for a marker written
 * before per-organization clients existed) the deploy's default. Nothing
 * when neither resolves, so the session is treated as signed out and the
 * user re-picks through the identifier-first page rather than the app
 * trying to initialize a client that can't exist.
 */
export const getActiveKeycloakClientConfig = ():
  | KeycloakClientConfig
  | undefined => {
  if (localStorage.getItem(ACTIVE_AUTH_PROVIDER_KEY) !== 'keycloak') {
    return undefined;
  }
  const stored = localStorage.getItem(ACTIVE_KEYCLOAK_CLIENT_KEY);
  if (stored) {
    try {
      return toKeycloakClientConfig(JSON.parse(stored));
    } catch {
      return undefined;
    }
  }
  return getDefaultKeycloakClientConfig();
};

export const getActiveAuthProvider = (): AuthProviderName => {
  return getActiveKeycloakClientConfig() ? 'keycloak' : 'auth0';
};

export function setActiveAuthProvider(provider: 'auth0'): void;
export function setActiveAuthProvider(
  provider: 'keycloak',
  client: KeycloakClientConfig
): void;
export function setActiveAuthProvider(
  provider: AuthProviderName,
  client?: KeycloakClientConfig
): void {
  localStorage.setItem(ACTIVE_AUTH_PROVIDER_KEY, provider);
  if (provider === 'keycloak' && client) {
    localStorage.setItem(ACTIVE_KEYCLOAK_CLIENT_KEY, JSON.stringify(client));
  } else {
    localStorage.removeItem(ACTIVE_KEYCLOAK_CLIENT_KEY);
  }
}

// Where to land after a Keycloak login. Auth0's SDK carries this for us as
// `appState.returnTo` (see AuthProvider.tsx); keycloak-js has no equivalent,
// so it's kept here across the redirect instead - and deliberately NOT in
// the redirect_uri itself, which is always getKeycloakRedirectUri(): OAuth
// forbids a fragment in redirect_uri (Keycloak answers "Invalid parameter:
// redirect_uri"), so a destination with a hash could never be sent that way.
// sessionStorage, not localStorage: it's this tab's login that's in flight.
const KEYCLOAK_RETURN_TO_KEY = 'confirm.keycloak_return_to';

export const getKeycloakRedirectUri = (): string =>
  `${window.location.origin}/`;

// The parameters Keycloak answers a login with, in the URL fragment
// (response_mode=fragment). keycloak-js strips them from the address bar
// with window.history.replaceState(), behind react-router's back - so the
// router's own `location.hash` still carries them, and any "come back here"
// URL built from it would too.
const KEYCLOAK_CALLBACK_PARAMS = [
  'code',
  'state',
  'session_state',
  'iss',
  'error',
  'error_description',
  'error_uri',
];

const stripKeycloakCallbackFromHash = (hash: string): string => {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  if (!params.has('state') || !(params.has('code') || params.has('error'))) {
    // An ordinary anchor - leave it exactly as written.
    return hash;
  }
  KEYCLOAK_CALLBACK_PARAMS.forEach((name) => params.delete(name));
  const remaining = params.toString();
  return remaining ? `#${remaining}` : '';
};

// A same-origin path + query + hash, safe to hand to history.replace(). An
// absolute URL to another origin (`continue` is a query parameter anyone can
// craft) falls back to the home page.
export const normalizeKeycloakReturnTo = (returnTo?: string): string => {
  let url: URL;
  try {
    url = new URL(returnTo ?? window.location.href, window.location.origin);
  } catch {
    return '/';
  }
  if (url.origin !== window.location.origin) {
    return '/';
  }
  return url.pathname + url.search + stripKeycloakCallbackFromHash(url.hash);
};

export const saveKeycloakReturnTo = (returnTo?: string): void => {
  try {
    sessionStorage.setItem(
      KEYCLOAK_RETURN_TO_KEY,
      normalizeKeycloakReturnTo(returnTo)
    );
  } catch {
    // Storage unavailable: the login still works, it just lands on the
    // home page.
  }
};

// Reads and clears in one go, so a destination is only ever used for the
// login it was saved for.
export const consumeKeycloakReturnTo = (): string | null => {
  try {
    const returnTo = sessionStorage.getItem(KEYCLOAK_RETURN_TO_KEY);
    sessionStorage.removeItem(KEYCLOAK_RETURN_TO_KEY);
    return returnTo;
  } catch {
    return null;
  }
};

// Kill switch for the whole identifier-first login page / Keycloak
// integration, independent of ACTIVE_AUTH_PROVIDER_KEY above (which only
// tracks an *already* Keycloak-authenticated session). REACT_APP_ENABLE_KEYCLOAK
// is the default for an environment (on for local dev, off everywhere else -
// see env-files/confirm-common.env); KEYCLOAK_OVERRIDE_KEY lets one browser
// opt in/out regardless of that default, so the feature can be tried in
// UAT/prod (`?keycloak=true`) without turning it on for every user there.
const KEYCLOAK_OVERRIDE_KEY = 'confirm.keycloak_override';
const KEYCLOAK_QUERY_PARAM = 'keycloak';

// Call once, as early as possible (before anything reads isKeycloakEnabled)
// so a `?keycloak=true`/`?keycloak=false` visit takes effect on that same
// page load, not just future ones.
export const applyKeycloakQueryOverride = (search: string): void => {
  const value = new URLSearchParams(search).get(KEYCLOAK_QUERY_PARAM);
  if (value === 'true' || value === 'false') {
    localStorage.setItem(KEYCLOAK_OVERRIDE_KEY, value);
  }
};

export const isKeycloakEnabled = (): boolean => {
  const override = localStorage.getItem(KEYCLOAK_OVERRIDE_KEY);
  if (override === 'true' || override === 'false') {
    return override === 'true';
  }
  return process.env.REACT_APP_ENABLE_KEYCLOAK === 'true';
};

// One keycloak-js instance per client (by issuer), created on first use.
// keycloak-js can only init() each instance once, so instances live for
// the page - a user who logs out of one realm and logs in to another on
// the same page load simply gets a second instance.
const instances = new Map<string, Keycloak>();

export const getKeycloakClient = (config: KeycloakClientConfig): Keycloak => {
  let client = instances.get(config.issuer);
  if (!client) {
    client = new Keycloak({
      url: config.url,
      realm: config.realm,
      clientId: config.clientId,
    });
    instances.set(config.issuer, client);
  }
  return client;
};

// keycloak-js requires init() to complete before login()/logout()/
// updateToken() are called - without it, internal state (endpoints, PKCE
// verifier, etc.) isn't set up and the SDK produces garbage output (e.g. a
// redirect_uri containing "[object Object]") instead of failing loudly.
//
// Shared/memoized per client so every caller - the mount-time check-sso
// effect for a *returning* Keycloak user, and loginWithRedirect() for a
// user choosing Keycloak for the first time - awaits the same single
// init() call (keycloak-js throws if init() is called more than once on an
// instance), while still never touching the network for the common
// Auth0-only case where nothing ever calls this.
//
// A failed init() is memoized too, deliberately: keycloak-js flags the
// instance as initialized before doing any work, so a retry on the same
// instance can only ever throw "can only be initialized once". Recovery is
// a page reload.
const initPromises = new Map<string, Promise<boolean>>();

// keycloak-js's check-sso runs in a hidden iframe pointed at the Keycloak
// server and resolves only when that iframe posts a message back. The SDK
// puts its own 10s timeout on the preceding third-party-cookie probe, but
// none on the silent-SSO iframe itself - so if Keycloak is unreachable,
// misconfigured (e.g. REACT_APP_KEYCLOAK_URL unset with `?keycloak=true`),
// or serves an error page instead of redirecting to
// /silent-check-sso.html, init() simply never settles and the app would sit
// on "Initializing..." forever. This is the upper bound on the whole init,
// sized to comfortably cover the SDK's own 10s probe timeout.
export const KEYCLOAK_INIT_TIMEOUT_MS = 15000;
export const KEYCLOAK_SCOPE = 'openid profile email organization';
// The one message the rest of the app (utils/util/error.ts) needs to know
// about for "Keycloak didn't come up in time" - both our own timeout above
// and keycloak-js's internal ones are surfaced under it, see
// normalizeKeycloakInitError().
export const KEYCLOAK_INIT_TIMEOUT_MESSAGE =
  'Timeout when waiting for Keycloak to initialize.';

// keycloak-js has its own internal timeout on the third-party-cookie probe
// that runs before check-sso, and rejects init() with a hardcoded, English,
// unversioned message ("Timeout when waiting for 3rd party check iframe
// message." as of 26.x). Re-wrap anything that looks like one of those as
// KEYCLOAK_INIT_TIMEOUT_MESSAGE here, so the exact SDK wording is coupled
// in this one place: if a future release rewords it, the only consequence
// is that the failure shows the generic auth error instead of the friendly
// timeout copy - nothing else in the app matches on SDK strings.
export const normalizeKeycloakInitError = (e: unknown): unknown => {
  if (e instanceof Error && e.message.startsWith('Timeout')) {
    return new Error(KEYCLOAK_INIT_TIMEOUT_MESSAGE, { cause: e });
  }
  return e;
};

export const ensureKeycloakInitialized = (
  config: KeycloakClientConfig
): Promise<boolean> => {
  let initPromise = initPromises.get(config.issuer);
  if (!initPromise) {
    let timeoutHandle: number | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutHandle = window.setTimeout(
        () => reject(new Error(KEYCLOAK_INIT_TIMEOUT_MESSAGE)),
        KEYCLOAK_INIT_TIMEOUT_MS
      );
    });
    initPromise = Promise.race([
      getKeycloakClient(config)
        .init({
          onLoad: 'check-sso',
          scope: KEYCLOAK_SCOPE,
          pkceMethod: 'S256',
          checkLoginIframe: false,
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        })
        .catch((e) => {
          throw normalizeKeycloakInitError(e);
        }),
      timeout,
    ]).finally(() => window.clearTimeout(timeoutHandle));
    initPromises.set(config.issuer, initPromise);
  }
  return initPromise;
};
